import type { wave } from '@round/api';
import { datadogRum } from '@datadog/browser-rum';
import { PLATFORM_LABEL } from './campaignDisplay';

export type PostUrlValidationResult = { isValid: true; url: string } | { isValid: false; message: string };

const POST_HOSTS_BY_PLATFORM: Record<wave.InvitePlatform, string[]> = {
    TIKTOK: ['tiktok.com', 'tt.site'],
    INSTAGRAM: ['instagram.com'],
    YOUTUBE: ['youtube.com', 'youtu.be'],
};

const TIKTOK_SHORT_LINK_HOSTS = ['vt.tiktok.com', 'vm.tiktok.com'];
const TIKTOK_SHORT_LINK_PATH = /^\/[\w-]+\/?$/;
const TIKTOK_POST_PATHS = [/^\/(?:@[\w.-]+\/)?(?:video|photo)\/\d+\/?$/, /^\/t\/[\w-]+\/?$/];
const INSTAGRAM_POST_PATHS = [/^\/(?:[\w.]+\/)?(?:p|reels?|tv)\/[\w-]+\/?$/, /^\/share\/(?:(?:p|reels?)\/)?[\w-]+\/?$/];

// Shape checks mirror SubmissionCreateSerializer: TikTok and Instagram reject
// non-post paths (a profile, for instance); YouTube is host-only on both sides.
const IS_POST_PATH_BY_PLATFORM: Record<wave.InvitePlatform, (url: URL) => boolean> = {
    TIKTOK: (url) =>
        TIKTOK_SHORT_LINK_HOSTS.includes(url.hostname.replace(/^www\./, ''))
            ? TIKTOK_SHORT_LINK_PATH.test(url.pathname)
            : TIKTOK_POST_PATHS.some((pattern) => pattern.test(url.pathname)),
    INSTAGRAM: (url) => INSTAGRAM_POST_PATHS.some((pattern) => pattern.test(url.pathname)),
    YOUTUBE: () => true,
};

const parseUrl = (value: string): URL | null => {
    try {
        return new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
    } catch {
        return null;
    }
};

export const validatePostUrl = (platform: wave.InvitePlatform, value: string): PostUrlValidationResult => {
    const label = PLATFORM_LABEL[platform];
    const url = parseUrl(value.trim());
    if (!url) {
        return { isValid: false, message: `Enter a link to your ${label} post.` };
    }
    const host = url.hostname.toLowerCase().replace(/^www\./, '');
    const isAllowedHost = POST_HOSTS_BY_PLATFORM[platform].some(
        (allowed) => host === allowed || host.endsWith(`.${allowed}`)
    );
    if (!isAllowedHost) {
        return { isValid: false, message: `Enter a link to your ${label} post.` };
    }
    if (!IS_POST_PATH_BY_PLATFORM[platform](url)) {
        return { isValid: false, message: "That doesn't look like a post URL." };
    }
    // Only www.tt.site resolves; the bare domain has no DNS record.
    if (url.hostname === 'tt.site') {
        url.hostname = 'www.tt.site';
    }
    return { isValid: true, url: url.href };
};

// Query strings carry share tokens, so only the origin and path are reported.
export const reportPostUrlValidationFailure = (postUrl: string, message: string): void => {
    const url = parseUrl(postUrl.trim());
    if (!url) {
        return;
    }
    datadogRum.addAction('fail local post url validation', { post_url: `${url.origin}${url.pathname}`, message });
};
